import NextLink from 'next/link'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { assetUrl } from 'utils/url'
import {
  useAPIPelaksanaanProgress,
  useAPIGetPengembanganKompetensi,
} from 'api/kinerja/useAPIKinerja'

export type PelaksanaanPegawaiCardProps = {
  className?: string
}

export function PelaksanaanPegawaiCard({
  className,
}: PelaksanaanPegawaiCardProps) {
  // -----------------------------------------
  // Get Pelaksanaan Progress Data
  // -----------------------------------------
  const { data: pelaksanaanProgressData } = useAPIPelaksanaanProgress({
    tipe: 'GPK',
    opts: {
      enabled: true,
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // Get pengembangan kompetensi data
  const { data: dataPengembanganKompetensi } = useAPIGetPengembanganKompetensi({
    params: {
      tipe: 'GPK',
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isTugasPokokDone =
    pelaksanaanProgressData?.data?.realisasiState?.primaryTask ===
      'SUBMITTED' ||
    pelaksanaanProgressData?.data?.realisasiState?.primaryTask === 'APPROVED'
  const isPengembanganKompetensiDone = ['SUBMITTED', 'APPROVED'].includes(
    dataPengembanganKompetensi?.data?.state
  )
  const isPraktikKinerjaDone =
    pelaksanaanProgressData?.data?.state === 'COMPLETED'

  return (
    <section
      className={clsx(
        'p-6 shadow-card rounded-lg bg-white space-y-6',
        className
      )}
    >
      <header className="flex items-center space-x-6">
        <img
          alt=""
          src={assetUrl('/illustrations/img-assessment-indicator.png')}
          width={84}
          height={84}
          className="flex-shrink-0"
        />

        <div className="space-y-1">
          <Text variant="heading-lg" as="h2">
            Pelaksanaan Kinerja
          </Text>
          <Text color="subdued">Laksanakan berdasarkan Perencanaan Anda.</Text>
        </div>
      </header>

      <div className="px-3 space-y-4">
        <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
          <img
            src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
            alt=""
            width={64}
            height={64}
            className="flex-shrink-0"
          />

          <Text variant="heading-sm" as="h3">
            Pelaksanaan Tugas Pokok
          </Text>

          <NextLink
            href={`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/tugas-pokok`}
            passHref
          >
            <Button
              as="a"
              type={undefined}
              color={isTugasPokokDone ? 'white' : 'black'}
              className="ml-auto min-w-40"
            >
              {isTugasPokokDone ? 'Cek' : 'Lakukan'}{' '}
              <span className="sr-only">Pelaksanaan Tugas Pokok</span>
            </Button>
          </NextLink>
        </div>

        <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
          <img
            src={assetUrl('/illustrations/img-badge-star.png')}
            alt=""
            width={64}
            height={64}
            className="flex-shrink-0"
          />

          <Text variant="heading-sm" as="h3">
            Praktik Kinerja
          </Text>

          <NextLink
            href={`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`}
            passHref
          >
            <Button
              as="a"
              type={undefined}
              color={isPraktikKinerjaDone ? 'white' : 'black'}
              className="min-w-40 ml-auto !inline-flex"
            >
              {isPraktikKinerjaDone ? 'Cek' : 'Lakukan'}{' '}
              <span className="sr-only">Praktik Kinerja</span>
            </Button>
          </NextLink>
        </div>

        <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
          <img
            src={assetUrl('/illustrations/img-document.png')}
            alt=""
            width={64}
            height={64}
            className="flex-shrink-0"
          />

          <Text variant="heading-sm" as="h3">
            Pengembangan Kompetensi
          </Text>

          <NextLink
            href={`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/pengembangan-kompetensi`}
            passHref
          >
            <Button
              as="a"
              type={undefined}
              color={isPengembanganKompetensiDone ? 'white' : 'black'}
              className="ml-auto min-w-40"
            >
              {isPengembanganKompetensiDone ? 'Cek' : 'Lakukan'}{' '}
              <span className="sr-only">Pengembangan Kompetensi</span>
            </Button>
          </NextLink>
        </div>

        <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
          <Text variant="heading-sm" as="h3">
            Perilaku Kerja
          </Text>

          <NextLink
            href={`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/perilaku-kerja`}
            passHref
          >
            <Button
              className="ml-auto min-w-40"
              as="a"
              type={undefined}
              color="white"
            >
              Cek <span className="sr-only">Perilaku Kerja</span>
            </Button>
          </NextLink>
        </div>
      </div>
    </section>
  )
}
